import React from "react";
import { useAssessment, PHASES } from "../context/AssessmentContext";
import { SIMULATOR_SUGGESTIONS } from "../data/simulator";
import PageWrapper from "../components/layout/PageWrapper";
import ScoreComparison from "../components/simulator/ScoreComparison";
import SuggestionToggle from "../components/simulator/SuggestionToggle";
import OptimizationTemplates from "../components/simulator/OptimizationTemplates";
import ImpactSummary from "../components/simulator/ImpactSummary";
import Footer from "../components/layout/Footer";
import { useToast } from "../components/ui/Toast";

export default function SimulatorPage() {
  const {
    answers,
    simAnswers,
    scoreResult,
    simScoreResult,
    appliedSuggestions,
    toggleSuggestion,
    applyTemplate,
    setPhase,
  } = useAssessment();

  const toast = useToast();

  if (!scoreResult || !simScoreResult) {
    return (
      <PageWrapper>
        <p style={{ color: "var(--text-muted)", textAlign: "center", marginTop: 80 }}>
          Complete the assessment first.{" "}
          <button onClick={() => setPhase(PHASES.LANDING)} className="btn btn-ghost" style={{ padding: "6px 14px", fontSize: 13 }}>
            Start Assessment
          </button>
        </p>
      </PageWrapper>
    );
  }

  const delta = simScoreResult.score - scoreResult.score;

  return (
    <>
    <PageWrapper maxWidth={860}>
      {/* ── Header ── */}
      <div className="fade-up" style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 14px",
          background: "rgba(99,102,241,0.1)",
          border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: 99,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "#a5b4fc",
          letterSpacing: "0.08em",
          marginBottom: 18,
        }}>
          🧪 WHAT IF IMPROVEMENT SIMULATOR
        </div>

        <h1 style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(24px, 3.5vw, 36px)",
          fontWeight: 700,
          color: "var(--text-primary)",
          letterSpacing: "-0.02em",
          marginBottom: 10,
        }}>
          Explore Your Potential Score
        </h1>

        <p style={{ fontSize: 14, color: "var(--text-muted)", maxWidth: 480, margin: "0 auto" }}>
          Toggle improvements below to see real-time score impact.
          Watch your tier change as you optimise each domain.
        </p>
      </div>

      {/* ── Navigation back ── */}
      <button
        onClick={() => setPhase(PHASES.RESULTS)}
        style={{
          background: "none",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          fontSize: 13,
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        ← Back to Results
      </button>

      {/* ── Score comparison ── */}
      <div className="fade-up-1" style={{ marginBottom: 28 }}>
        <ScoreComparison
          originalResult={scoreResult}
          simResult={simScoreResult}
        />
      </div>

      {/* ── Impact summary (shows only when improvements applied) ── */}
      {appliedSuggestions.size > 0 && (
        <div style={{ marginBottom: 24 }}>
          <ImpactSummary
            originalScore={scoreResult.score}
            simScore={simScoreResult.score}
            appliedCount={appliedSuggestions.size}
          />
        </div>
      )}

      {/* ── Quick templates ── */}
      <div className="fade-up-2" style={{ marginBottom: 24 }}>
        <OptimizationTemplates
          answers={answers}
          onApplyTemplate={applyTemplate}
        />
      </div>

      {/* ── Suggestion toggles ── */}
      <div className="fade-up-2" style={{ marginBottom: 32 }}>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--text-muted)",
          letterSpacing: "0.1em",
          marginBottom: 12,
        }}>
          AVAILABLE IMPROVEMENTS ({SIMULATOR_SUGGESTIONS.length} total)
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {SIMULATOR_SUGGESTIONS.map((s) => {
            const originalAnswer = answers[s.qId];
            const isRelevant = originalAnswer && s.triggerValues.includes(originalAnswer.value);
            const isApplied = appliedSuggestions.has(s.id);

            return (
              <SuggestionToggle
                key={s.id}
                suggestion={s}
                isApplied={isApplied}
                isRelevant={!!isRelevant}
                onToggle={toggleSuggestion}
              />
            );
          })}
        </div>
      </div>

      {/* ── Conversion CTAs ── */}
      {/* Source: Section 5 — Conversion Integration */}
      <div className="fade-up-3" style={{
        padding: "28px 24px",
        background: "var(--bg-card-hover)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        marginBottom: 20,
      }}>
        <div style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 16,
          color: "var(--accent-indigo)",
          marginBottom: 6,
        }}>
          Ready to achieve your simulated score?
        </div>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 18 }}>
          {delta > 0
            ? `Your simulation shows a potential improvement of +${delta} points. A Ballot D.A. consultant can build you a roadmap to get there.`
            : "Toggle improvements above to see your potential score, then book a session to build your roadmap."}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {[
            { icon: "📅", label: "Schedule Optimisation Session",  sublabel: "Get your roadmap", primary: true },
            { icon: "🎬", label: "See Product Demos",              sublabel: "How we deliver this" },
            { icon: "📥", label: "Download Improvement Blueprint", sublabel: "PDF with steps" },
          ].map((cta) => (
            <button
              key={cta.label}
              className={`btn ${cta.primary ? "btn-primary" : "btn-ghost"}`}
              style={{
                flexDirection: "column",
                gap: 4,
                padding: "14px",
                height: "auto",
                fontSize: 12,
              }}
              onClick={() => toast.info(`${cta.label} — connect your CRM/calendar integration here`)}
            >
              <span style={{ fontSize: 18 }}>{cta.icon}</span>
              <span style={{ fontWeight: 700 }}>{cta.label}</span>
              <span style={{
                fontSize: 10,
                opacity: 0.65,
                fontWeight: 400,
              }}>
                {cta.sublabel}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Reset ── */}
      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => setPhase(PHASES.RESULTS)}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          ← Return to full results
        </button>
      </div>
    </PageWrapper>
    <Footer />
    </>
  );
}
